import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { useAuth } from "@/contexts/AuthContext";
import { CAUSE_NAMES } from "@/lib/causes";
import { Search, Download, Users, Clock, ShoppingBag, Sparkles, ArrowRight, Mail, Inbox, Copy, CheckCheck, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsUnread, setContactsUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("created_at");
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("members"); // members | contacts
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, username, name}
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") {
      Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/contacts"),
      ]).then(([s, u, c]) => {
        setStats(s.data);
        setUsers(u.data);
        setContacts(c.data.messages || []);
        setContactsUnread(c.data.unread_count || 0);
        setLoading(false);
      });
    }
  }, [user]);

  const markContactRead = async (id) => {
    try {
      await api.post(`/admin/contacts/${id}/read`);
      setContacts((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
      setContactsUnread((n) => Math.max(0, n - 1));
    } catch {
      /* ignore */
    }
  };

  const copyAllEmails = async () => {
    const emails = users.filter((u) => u.email).map((u) => u.email).join(", ");
    if (!emails) {
      toast.error("No members have shared their email yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${emails.split(",").length} emails to clipboard`);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const deleteMember = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success(
        `Deleted @${data.deleted_user}` +
          (data.cascade.signups + data.cascade.transactions + data.cascade.listings + data.cascade.opportunities > 0
            ? ` and ${data.cascade.signups} signup(s), ${data.cascade.transactions} tx, ${data.cascade.listings} listing(s), ${data.cascade.opportunities} opp(s)`
            : "")
      );
      setUsers((prev) => prev.filter((u) => u.id !== confirmDelete.id));
      setStats((prev) => prev && { ...prev, total_users: Math.max(0, prev.total_users - 1) });
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = users;
    if (term) {
      list = users.filter(
        (u) =>
          u.username?.toLowerCase().includes(term) ||
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.primary_cause?.toLowerCase().includes(term),
      );
    }
    const sorted = [...list].sort((a, b) => {
      if (sort === "balance") return (b.time_hours_balance || 0) - (a.time_hours_balance || 0);
      if (sort === "shifts") return (b.stats?.shifts_credited || 0) - (a.stats?.shifts_credited || 0);
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return sorted;
  }, [users, search, sort]);

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const exportCsv = () => {
    const rows = [
      ["username", "name", "email", "role", "primary_cause", "time_hours_balance", "assessment_completed", "shifts_credited", "shifts_pending", "purchases_made", "sales_completed", "created_at"],
      ...filtered.map((u) => [
        u.username,
        (u.name || "").replace(/,/g, " "),
        u.email || "",
        u.role,
        u.primary_cause || "",
        u.time_hours_balance,
        u.assessment_completed,
        u.stats?.shifts_credited || 0,
        u.stats?.shifts_pending || 0,
        u.stats?.purchases_made || 0,
        u.stats?.sales_completed || 0,
        u.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bahoi-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = async (userId) => {
    setDetail({ loading: true });
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetail(data);
    } catch (e) {
      setDetail(null);
    }
  };

  return (
    <Layout>
      <div data-testid="admin-page" className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <span className="overline text-[var(--cm-primary)]">Admin</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">Community CRM</h1>
            <p className="text-ink-soft mt-2 max-w-xl">Everyone who has joined Bahoi, what they've done, what they're owed.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-copy-emails" onClick={copyAllEmails} className="btn-outline inline-flex items-center gap-2 text-sm">
              <Copy className="h-4 w-4" /> Copy all emails
            </button>
            <button data-testid="admin-export-csv" onClick={exportCsv} className="btn-outline inline-flex items-center gap-2 text-sm">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard testid="stat-total-users" icon={Users} label="Total members" value={stats.total_users} sub={`${stats.users_with_email || 0} shared email · +${stats.new_users_week} this week`} />
            <StatCard testid="stat-hours-credited" icon={Clock} label="Hours credited" value={Number(stats.total_hours_credited).toFixed(1)} sub={`${stats.credited_signups} verified shifts`} />
            <StatCard testid="stat-purchases" icon={ShoppingBag} label="Marketplace orders" value={stats.total_purchases} sub={`${stats.completed_purchases} completed`} />
            <StatCard testid="stat-pending" icon={Sparkles} label="Pending verification" value={stats.pending_verifications} sub={`across ${stats.total_opportunities} opportunities`} />
          </div>
        )}

        <div className="flex items-center gap-2 mb-6 border-b border-ink-soft">
          <TabBtn active={tab === "members"} onClick={() => setTab("members")} testid="admin-tab-members">
            Members <span className="text-xs text-ink-soft">({users.length})</span>
          </TabBtn>
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="admin-tab-contacts">
            <Inbox className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
            Contact Inbox
            {contactsUnread > 0 && (
              <span data-testid="admin-contacts-unread-dot" className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-[var(--cm-primary)] text-white px-1.5">
                {contactsUnread}
              </span>
            )}
          </TabBtn>
        </div>

        {tab === "members" && (
        <>
        <div className="card-soft p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username, name, email, or cause…"
              className="w-full pl-10 pr-4 py-2.5 bg-app rounded-xl border border-ink-soft focus:ring-2 focus:ring-[var(--cm-primary)]/40"
            />
          </div>
          <select
            data-testid="admin-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2.5 bg-app rounded-xl border border-ink-soft text-sm"
          >
            <option value="created_at">Newest first</option>
            <option value="balance">Highest balance</option>
            <option value="shifts">Most shifts</option>
            <option value="name">Name (A–Z)</option>
          </select>
          <span className="text-xs text-ink-soft" data-testid="admin-result-count">
            Showing {filtered.length} of {users.length}
          </span>
        </div>

        {loading ? (
          <div className="text-ink-soft" data-testid="admin-loading">Loading members…</div>
        ) : (
          <div className="card-soft overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-app">
                <tr className="text-left">
                  <Th>Member</Th>
                  <Th>Email</Th>
                  <Th>Cause</Th>
                  <Th>Balance</Th>
                  <Th>Shifts</Th>
                  <Th>Marketplace</Th>
                  <Th>Joined</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-t border-ink-soft hover:bg-app/60" data-testid={`admin-user-row-${u.username}`}>
                    <Td>
                      <div className="font-display font-semibold">{u.name || u.username}</div>
                      <div className="text-xs text-ink-soft">@{u.username}{u.role === "admin" ? " · admin" : ""}</div>
                    </Td>
                    <Td>
                      {u.email ? (
                        <a href={`mailto:${u.email}`} className="text-[var(--cm-primary)] text-xs hover:underline" data-testid={`admin-email-${u.username}`}>
                          {u.email}
                        </a>
                      ) : (
                        <span className="text-xs text-ink-soft italic">—</span>
                      )}
                    </Td>
                    <Td>
                      {u.primary_cause ? (
                        <span className="pill">{CAUSE_NAMES[u.primary_cause] || u.primary_cause}</span>
                      ) : (
                        <span className="text-xs text-ink-soft italic">No assessment</span>
                      )}
                    </Td>
                    <Td><TimeBadge hours={u.time_hours_balance} size="sm" /></Td>
                    <Td>
                      <span className="text-ink font-semibold">{u.stats.shifts_credited}</span>
                      <span className="text-ink-soft"> credited</span>
                      {u.stats.shifts_pending > 0 && (
                        <span className="text-amber-700"> · {u.stats.shifts_pending} pending</span>
                      )}
                    </Td>
                    <Td>
                      <span className="text-ink-soft text-xs">
                        {u.stats.purchases_made} buys · {u.stats.sales_completed} sales
                      </span>
                    </Td>
                    <Td><span className="text-xs text-ink-soft">{u.created_at?.slice(0, 10)}</span></Td>
                    <Td>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`admin-view-${u.username}`}
                          onClick={() => openDetail(u.id)}
                          className="text-[var(--cm-primary)] font-semibold text-xs inline-flex items-center gap-1"
                        >
                          View <ArrowRight className="h-3 w-3" />
                        </button>
                        {u.role !== "admin" && (
                          <button
                            data-testid={`admin-delete-${u.username}`}
                            onClick={() => setConfirmDelete({ id: u.id, username: u.username, name: u.name || u.username })}
                            className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
                            title="Delete this member and their data"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><Td colSpan={8}><div className="text-center py-8 text-ink-soft">No members match your search.</div></Td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {tab === "contacts" && (
          <div data-testid="admin-contacts-panel" className="card-soft p-4">
            {contacts.length === 0 ? (
              <div className="text-center py-12 text-ink-soft">
                <Mail className="h-8 w-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No contact messages yet.</p>
                <p className="text-xs mt-1">When someone submits the form on <Link to="/contact" className="underline">/contact</Link>, it'll show up here.</p>
              </div>
            ) : (
              <div className="divide-y divide-ink-soft">
                {contacts.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`admin-contact-${m.id}`}
                    className={`p-4 ${m.read ? "opacity-70" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-display font-semibold">{m.name}</span>
                          {!m.read && <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--cm-primary)] text-white font-bold uppercase tracking-wider">new</span>}
                          {m.email_sent && <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--cm-secondary)]/15 text-[var(--cm-secondary)] font-semibold uppercase tracking-wider" title="Email also sent to bahoimatch@gmail.com">emailed</span>}
                        </div>
                        <a href={`mailto:${m.email}?subject=Re: Your message to Bahoi`} className="text-xs text-[var(--cm-primary)] hover:underline">
                          {m.email}
                        </a>
                      </div>
                      <div className="text-xs text-ink-soft">
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                    <p className="text-sm mt-3 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                    <div className="mt-3 flex items-center gap-3">
                      {!m.read && (
                        <button
                          data-testid={`admin-mark-read-${m.id}`}
                          onClick={() => markContactRead(m.id)}
                          className="text-xs font-semibold text-ink-soft hover:text-ink inline-flex items-center gap-1"
                        >
                          <CheckCheck className="h-3 w-3" /> Mark as read
                        </button>
                      )}
                      <a
                        data-testid={`admin-reply-${m.id}`}
                        href={`mailto:${m.email}?subject=Re: Your message to Bahoi`}
                        className="text-xs font-semibold text-[var(--cm-primary)] hover:underline inline-flex items-center gap-1"
                      >
                        Reply <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {confirmDelete && (
          <div
            data-testid="admin-delete-modal"
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !deleting && setConfirmDelete(null)}
          >
            <div
              className="bg-surface rounded-2xl max-w-md w-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-12 w-12 rounded-full bg-red-100 grid place-items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-700" />
              </div>
              <h2 className="font-display font-bold text-2xl">Delete @{confirmDelete.username}?</h2>
              <p className="text-ink-soft mt-3 leading-relaxed text-sm">
                This permanently removes <span className="font-semibold text-ink">{confirmDelete.name}</span> from Bahoi, including their volunteer signups, marketplace transactions, listings, and any opportunities they organized.
              </p>
              <p className="text-xs text-red-700 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <strong>This cannot be undone.</strong> If they've done volunteer shifts you're relying on, their history disappears too.
              </p>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  data-testid="admin-delete-cancel"
                  onClick={() => setConfirmDelete(null)}
                  disabled={deleting}
                  className="btn-outline text-sm disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  data-testid="admin-delete-confirm"
                  onClick={deleteMember}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#c8322d" }}
                >
                  <Trash2 className="h-4 w-4" /> {deleting ? "Deleting…" : "Yes, delete permanently"}
                </button>
              </div>
            </div>
          </div>
        )}


        {detail && (
          <div
            data-testid="admin-detail-modal"
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDetail(null)}
          >
            <div
              className="bg-surface rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              {detail.loading ? (
                <div className="text-ink-soft">Loading…</div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h2 className="font-display font-bold text-2xl">{detail.user.name}</h2>
                      <p className="text-xs text-ink-soft mt-1">@{detail.user.username} · joined {detail.user.created_at?.slice(0, 10)}</p>
                      {detail.user.email && (
                        <a href={`mailto:${detail.user.email}`} className="inline-flex items-center gap-1.5 text-xs text-[var(--cm-primary)] mt-2 hover:underline" data-testid="admin-detail-email">
                          <Mail className="h-3 w-3" /> {detail.user.email}
                        </a>
                      )}
                    </div>
                    <TimeBadge hours={detail.user.time_hours_balance} />
                  </div>
                  <div className="text-sm text-ink-soft mb-6">
                    Primary cause: <span className="text-ink font-semibold">{CAUSE_NAMES[detail.user.primary_cause] || "—"}</span>
                    {" · "}Trust: {detail.trust.is_new_member ? "new member" : `${detail.trust.score}%`}
                  </div>
                  <h3 className="font-display font-semibold text-sm overline text-[var(--cm-primary)] mb-2">Recent shifts ({detail.signups.length})</h3>
                  <div className="space-y-2 mb-6">
                    {detail.signups.slice(0, 8).map((s) => (
                      <div key={s.id} className="text-xs flex justify-between border-b border-ink-soft py-2">
                        <span>{s.opportunity_title}</span>
                        <span className="text-ink-soft">{s.status}</span>
                      </div>
                    ))}
                    {detail.signups.length === 0 && <p className="text-xs text-ink-soft italic">No shifts yet.</p>}
                  </div>
                  <h3 className="font-display font-semibold text-sm overline text-[var(--cm-primary)] mb-2">Recent transactions ({detail.transactions.length})</h3>
                  <div className="space-y-2">
                    {detail.transactions.slice(0, 8).map((t) => (
                      <div key={t.id} className="text-xs flex justify-between border-b border-ink-soft py-2">
                        <span>{t.description || t.listing_title || t.kind}</span>
                        <span className="text-ink-soft">{Number(t.hours).toFixed(1)} hrs · {t.status}</span>
                      </div>
                    ))}
                    {detail.transactions.length === 0 && <p className="text-xs text-ink-soft italic">No transactions yet.</p>}
                  </div>
                  <button data-testid="admin-detail-close" onClick={() => setDetail(null)} className="btn-outline mt-6 text-sm">Close</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <Icon className="h-4 w-4 text-[var(--cm-primary)]" />
      <div className="mt-3">
        <div className="font-display font-bold text-3xl">{value}</div>
        <div className="text-xs uppercase tracking-widest text-ink-soft mt-1">{label}</div>
        {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold text-ink-soft">{children}</th>;
}
function Td({ children, colSpan }) {
  return <td className="px-4 py-3 align-top" colSpan={colSpan}>{children}</td>;
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-semibold transition-colors -mb-px border-b-2 ${
        active
          ? "border-[var(--cm-primary)] text-[var(--cm-primary)]"
          : "border-transparent text-ink-soft hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}
